import { useMemo } from 'react';
import { useTimezone } from '@/contexts/TimezoneContext';
import {
  nowInTimezone,
  startOfDayInTz,
  endOfDayInTz,
  startOfWeekInTz,
  endOfWeekInTz,
  startOfMonthInTz,
  endOfMonthInTz,
  subDays,
  subMonths,
  formatInTz,
  parseDateInTz,
  formatDateInputInTz,
  toTimezone,
  fromTimezone,
} from '@/utils/timezone';
import { getTimezoneShortCode } from '@/data/timezones';

/**
 * Hook that provides timezone-aware date utilities using the company's configured timezone.
 * All functions return dates appropriate for database queries (UTC) or display (formatted strings).
 */
export function useTimezoneUtils() {
  const { timezone, isLoading } = useTimezone();

  // Memoize so function references stay stable across renders — callers that
  // put these in useEffect deps (e.g. form.reset on dialog open) would otherwise loop.
  return useMemo(() => ({
    timezone,
    isLoading,
    timezoneShortCode: getTimezoneShortCode(timezone),
    now: () => nowInTimezone(timezone),
    startOfDay: (date: Date) => startOfDayInTz(date, timezone),
    endOfDay: (date: Date) => endOfDayInTz(date, timezone),
    startOfWeek: (date: Date) => startOfWeekInTz(date, timezone),
    endOfWeek: (date: Date) => endOfWeekInTz(date, timezone),
    startOfMonth: (date: Date) => startOfMonthInTz(date, timezone),
    endOfMonth: (date: Date) => endOfMonthInTz(date, timezone),
    subDays: (date: Date, days: number) => subDays(date, days),
    subMonths: (date: Date, months: number) => subMonths(date, months),
    formatDate: (date: Date | string, format: string) => formatInTz(date, timezone, format),
    parseDate: (dateStr: string) => parseDateInTz(dateStr, timezone),
    formatDateInput: (date: Date) => formatDateInputInTz(date, timezone),
    toTimezone: (date: Date | string) => toTimezone(date, timezone),
    fromTimezone: (date: Date) => fromTimezone(date, timezone),
  }), [timezone, isLoading]);
}
